import type { Prettify } from "@zetalabs/core";
import type { components, operations } from "../generated/schema";

export { SyncActionType, ThreadStatus, ThreadType } from "../generated/schema";

export * from "../generated/schema";

export type LinkedAccount = components["schemas"]["APIAccount"];
export type PricingVariantResponse =
	components["schemas"]["PricingVariantResponse"];

export type MetadataResponse = components["schemas"]["MetadataResponse"];
export type SyncAction = components["schemas"]["SubSyncAction"];
export type ThreadMessage =
	components["schemas"]["ThreadMessagesGetResponse"][number];
export type GmailLabel = components["schemas"]["GmailLabel"];
export type GmailLabelColor = components["schemas"]["GmailLabelColor"];
export type GmailLabelVisibilityToggle =
	components["schemas"]["GmailLabelVisibilityToggle"];

export type GetThreadsResponse =
	components["schemas"]["ThreadMessagesGetResponse"];
export type ThreadMessages = ThreadMessage[];
export type ThreadMessageRole = ThreadMessage["role"];
export type Thread = components["schemas"]["Thread"];
export type Threads = Thread[];

// // TODO: Update these types once the backend schema is updated
// export interface GetThreadsOptions {
//   after_timestamp?: string | null;
// }

export type FilterView = components["schemas"]["FilterView"];
export type FilterViews = FilterView[];
export type FilterViewCreate = components["schemas"]["FilterViewCreate"];
export type FilterViewUpdate = components["schemas"]["FilterViewUpdate"];

export type ErrorMessage = components["schemas"]["ErrorMessage"];
export type StreamErrorMessage = components["schemas"]["StreamErrorMessage"];

export type ReadStatus = components["schemas"]["ReadStatus"];

// `image_file_uris` should not be visible in the client
// and only needed to inject images through a server-side
// generated user message.
export type UserMessage = components["schemas"]["UserMessage"];
export type AssistantTokenMessage =
	components["schemas"]["AssistantTokenMessage"];
export type AssistantMessage = components["schemas"]["AssistantMessage"];

export type AutosuggestsMessage = components["schemas"]["AutosuggestsMessage"];

export type InputWidgetMessage = components["schemas"]["InputWidgetMessage"];

export type InputWidget = components["schemas"]["InputWidget"];

export type CredentialsRequestMessage =
	components["schemas"]["CredentialRequestMessage"];

export type ApprovalRequestMessage =
	components["schemas"]["ApprovalRequestMessage"];

export type ApprovalSuccessMessage =
	components["schemas"]["ApprovalSuccessMessage"];

export type CredentialSuccessMessage =
	components["schemas"]["CredentialSuccessMessage"];

export type SuccessMessage = ApprovalSuccessMessage | CredentialSuccessMessage;

export type SuccessMessageRole = SuccessMessage["role"];

export type RequestMessage = CredentialsRequestMessage | ApprovalRequestMessage;

export type RequestMessageRole = RequestMessage["role"];

export type ServerWidgetMessage =
	| InputWidgetMessage
	| ApprovalRequestMessage
	| ApprovalSuccessMessage
	| CredentialsRequestMessage
	| CredentialSuccessMessage;

export type ServerReceivedEmailMessage =
	components["schemas"]["ReceivedEmailMessage"];

export type DraftUseWebAgent = components["schemas"]["DraftUseWebAgent"];

export type DraftSendEmail = components["schemas"]["DraftSendEmail"];

export type DraftSuggestTask = components["schemas"]["DraftSuggestTask"];

export type ServerEmailDraftMessage = components["schemas"]["DraftMessage"];
export type ServerEmailDraftStatus = ServerEmailDraftMessage["status"];

export type ServerDraftMessageStatus = components["schemas"]["DraftStatus"];

export type ServerEmailDraftSendMessage =
	components["schemas"]["DraftSendEmail"];

export type ServerEditDraftMessage = components["schemas"]["EditDraftsMessage"];

export type ServerDraftChangeSuggestionMessage =
	components["schemas"]["DraftChangeSuggestionMessage"];

export type ServerDraftNotificationMessage =
	components["schemas"]["DraftNotificationMessage"];

export type ServerDraftOptionSuggestionMessage =
	components["schemas"]["DraftOptionSuggestionMessage"];

export type ServerDisplayMessage =
	| ServerDraftChangeSuggestionMessage
	| ServerEditDraftMessage
	| ServerReceivedEmailMessage
	| ServerEmailDraftMessage
	| ServerDraftNotificationMessage
	| ServerDraftOptionSuggestionMessage;

export type ServerSnippetMessage = components["schemas"]["SnippetMessage"];

export type ServerContactSnippetData = components["schemas"]["Contact"];

export type ServerGoogleCalendarSnippetData =
	components["schemas"]["GoogleCalendarSnippet"];

export type ServerAssistantMessage = AssistantMessage | AssistantTokenMessage;

export type SubscriptionProduct =
	components["schemas"]["SubscriptionProductId"];

// Tools
export type ToolCallMessageUnion = components["schemas"]["ToolCallMessage"];

export type ToolCallSummaryMessage =
	components["schemas"]["ToolCallSummaryMessage"];

export type ToolUpdateMessage = components["schemas"]["ToolUpdateMessage"];
export type ToolResponseMessage = components["schemas"]["ToolResponseMessage"];

export type NoMoreToolCallsMessage =
	components["schemas"]["NoMoreToolCallsMessage"];

export type ToolCallPreparationMessage =
	components["schemas"]["ToolCallPreparationMessage"];

export type ToolReponseContent = ToolResponseMessage["content"];

export type WebAgentProgressMessage =
	components["schemas"]["WebAgentProgressMessage"];

export type WebAgentActionStep = components["schemas"]["WebAgentActionStep"];

export type PlanGoalSimple = components["schemas"]["PlanGoalSimple"];

export type ToolCallShared = Omit<ToolCallMessageUnion, "tool">;

export type UseWebAgentTool = components["schemas"]["UseWebAgent"];
export type QuickAiSearchTool = components["schemas"]["QuickAiSearch"];

export enum QuickAiSearchRole {
	quick_ai_search = "quick_ai_search",
}

export enum UseWebAgentRole {
	use_web_agent = "use_web_agent",
}

export type CodeInterpreter = components["schemas"]["CodeInterpreter"];
export type SendEmail = components["schemas"]["SendEmail"];
export type CancelTool = components["schemas"]["CancelTool"];
export type RecurringActivation = components["schemas"]["RecurringActivation"];
export type WaitTool = components["schemas"]["Wait"];

export type WebAgentMessage = Prettify<
	ToolCallShared & {
		tool: UseWebAgentTool;
	}
>;

export type QuickAiSearchMessage = Prettify<
	ToolCallShared & {
		tool: QuickAiSearchTool;
	}
>;

export type CodeInterpreterMessage = Prettify<
	ToolCallShared & {
		tool: CodeInterpreter;
	}
>;

export type SendEmailMessage = Prettify<
	ToolCallShared & {
		tool: SendEmail;
	}
>;

export type CancelToolMessage = Prettify<
	ToolCallShared & {
		tool: CancelTool;
	}
>;

export type RecurringActivationMessage = Prettify<
	ToolCallShared & {
		tool: RecurringActivation;
	}
>;

export type WaitToolMessage = Prettify<
	ToolCallShared & {
		tool: WaitTool;
	}
>;

export type ToolCallMessage =
	| WebAgentMessage
	| QuickAiSearchMessage
	| SendEmailMessage
	| WaitToolMessage
	| RecurringActivationMessage
	| CancelToolMessage
	| CodeInterpreterMessage;

export type ServerToolMessage =
	| ToolCallMessageUnion
	| ToolCallPreparationMessage
	| ToolCallSummaryMessage
	| ToolResponseMessage
	| WebAgentProgressMessage
	| ToolUpdateMessage;

// API Calls
export type GetAllThreadsQuery =
	operations["get_all_threads"]["parameters"]["query"];

export type CreateThreadMessageInput =
	components["schemas"]["ThreadMessageCreateRequest"];

export type CreateThreadInput = components["schemas"]["ThreadCreateRequest"];

export type CreateThreadMessageResponse =
	components["schemas"]["ThreadMessageCreateResponse"];

export type CreateThreadResponse =
	components["schemas"]["ThreadCreateResponse"];

export type CreateCredentialsInput =
	components["schemas"]["APIAddCredentialInput"];

export type AddCredentialsInput = Omit<CreateCredentialsInput, "user_id">;

export type DeleteCredenntialsInput =
	components["schemas"]["APIDeleteCredentialInput"];

export type RequestCredentialsInput =
	components["schemas"]["APIRequestCredentialInputModel"];

export type SubscriptionCheckoutSessionInput =
	components["schemas"]["APISubscriptionCheckoutSessionInput"];

export type SubscriptionSessionUrlResponse =
	components["schemas"]["APISubscriptionSessionUrlResponse"];

export type UserSubscriptionResponse =
	components["schemas"]["APIUserSubscriptionResponse"];

export type CreateThreadMessageInputWithFiles = Prettify<
	CreateThreadMessageInput & {
		files: FileMetadata[];
	}
>;

// Widget Inputs
export type PrimitiveInput = components["schemas"]["PrimitiveInputRole"];
export type SelectionInput = components["schemas"]["SelectInputRole"];

export type ExtendedPrimitiveInput = Prettify<
	Omit<PrimitiveInput, "type"> & {
		type: PrimitiveInput["type"] | "email" | "password";
		name: string;
		required: boolean;
	}
>;

export interface SubmitInput {
	role: "submit_input";
	label: string;
	disclaimer?: string;
}

export type ExtendedSelectionInput = Prettify<
	SelectionInput & {
		name: string;
		required: boolean;
	}
>;

export type FormField = ExtendedPrimitiveInput | ExtendedSelectionInput;

export type FormFieldType = FormField["role"];

export type UniqueMessage = AutosuggestsMessage; // |  WidgetMessage;
export type UniqueMessageType = UniqueMessage["role"];

export type PrimitiveWidgetResponse =
	components["schemas"]["PrimitiveInputResponse"];
export type SelectInputResponse = components["schemas"]["SelectInputResponse"];
export type InputWidgetResponse = PrimitiveWidgetResponse | SelectInputResponse;

export type ApiMeReponse = components["schemas"]["APIMeResponse"];

export type ReferralCodeInput = components["schemas"]["ReferralCodeInput"];

export type FileMetadata = components["schemas"]["FileMetadata"];

export type UploadUrlResponse = components["schemas"]["UploadUrlPostResponse"];

export type DomainCredentials = components["schemas"]["DomainCredentials"];
export type APIGetCredentialsOutput =
	components["schemas"]["APIGetCredentialsOutput"];

export type IntegrationResponse = components["schemas"]["IntegrationResponse"];

export type BackfillStatusResponse =
	components["schemas"]["InboxBackfillStatusResponse"];

// Inbox
export type ServerCalendarUpsertEventMessage = Required<
	components["schemas"]["GoogleCalendarUpsertEvent"]
>;

export type DraftGoogleCalendarUpsertEvent = Required<
	components["schemas"]["DraftGoogleCalendarUpsertEvent"]
>;

export type DraftUpsertRule = Required<
	components["schemas"]["DraftUpsertRule"]
>;

export type DraftUpsertRuleInfo = components["schemas"]["DraftUpsertRuleInfo"];

export type DraftSuggestTaskInfo =
	components["schemas"]["DraftSuggestTaskInfo"];

export type ServerDraftMessage =
	| ServerEmailDraftMessage
	| ServerEditDraftMessage;

export type GetSendAsApiResponse =
	operations["get_send_as"]["responses"]["200"]["content"]["application/json"];
